import { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Grid,
  GridItem,
  VStack,
  Button,
  Text,
  Spinner,
  Center,
  useToast,
  background,
} from "@chakra-ui/react";
import DynamicFormContainer from "components/CommonForm/DynamicFormContainer";
import ProfileCard from "components/CommonForm/ProfileCard";
import { clientsApiAction } from "stores/redux/apiSlices/clientApi/clientApiSlice";
import { toastActions } from "stores/redux/slices/toastSlice";
import { productsApiAction } from "stores/redux/apiSlices/productApi/productApiSlice";
import { driversApiAction } from "stores/redux/apiSlices/driverApi/driverApiSlice";
import CustomTimePicker from "./CustomTimePicker";
// import TimePicker from 'react-time-picker';

const AddCustomer = ({ customerData, onAddCustomerStatus }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const [addClientApi] = clientsApiAction.addClientApi();
  const [updateClientApi] = clientsApiAction.updateClientApi();
  const [resetPassword] = clientsApiAction.resetPasswordApi();

  const [loadingDrivers, setLoadingDrivers] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [value, setValue] = useState("10:00");
  const [time, setTime] = useState("");

  const driverFilter = useMemo(() => ({ role: "DRIVER", isActive: true }), []);
  const {
    data: driverData,
    refetch: refetchDriver,
    isLoading: isDriversLoading,
  } = driversApiAction.getDriversListApi(driverFilter, {
    refetchOnMountOrArgChange: true,
  });

  const filters = useMemo(() => ({ isActive: true }), []);
  const {
    data: productData,
    refetch: refetchProducts,
    isLoading: isProductsLoading,
  } = productsApiAction.getProductsListApi(filters, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    refetchDriver();
    refetchProducts();
  }, [refetchDriver, refetchProducts]);

  useEffect(() => {
    setLoadingDrivers(isDriversLoading);
  }, [isDriversLoading]);

  useEffect(() => {
    setLoadingProducts(isProductsLoading);
  }, [isProductsLoading]);

  const driverListData = driverData?.data?.users || [];

  const productsListData = productData?.data?.products || [];

  const driverOptions = useMemo(() => {
    return driverListData.map((driver) => ({
      label: driver.name,
      value: driver.id,
    }));
  }, [driverListData]);

  const productOptions = useMemo(() => {
    return productsListData.map((product) => ({
      label: product.name,
      value: product.productId,
    }));
  }, [productsListData]);
  const isEdit = !!customerData?.id;
  const convert12to24 = (timeStr) => {
    if (!timeStr || typeof timeStr !== "string") return "";
    const [time, modifier] = timeStr.trim().split(" ");
    if (!time || !modifier) return "";
    let [hours, minutes] = time.split(":").map(Number);
    if (modifier.toUpperCase() === "PM" && hours !== 12) hours += 12;
    if (modifier.toUpperCase() === "AM" && hours === 12) hours = 0;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;
  };
  const convert24to12 = (timeStr) => {
    if (!timeStr || typeof timeStr !== "string") return "";

    // Accept HH:mm or HH:mm:ss
    const [hourStr, minuteStr = "00"] = timeStr.split(":");
    const hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);
    if (isNaN(hour) || isNaN(minute)) return "";

    const ampm = hour >= 12 ? "PM" : "AM";
    const adjustedHour = hour % 12 || 12;

    return `${adjustedHour
      .toString()
      .padStart(2, "0")}:${minute.toString().padStart(2, "0")} ${ampm}`;
  };

  const formConfig = [
    {
      id: "basicInfo",
      title: "Βασικές Πληροφορίες",
      fields: [
        {
          name: "fullname",
          label: "Ονοματεπώνυμο",
          type: "text",
          required: true,
          gridSize: 6,
        },
        {
          name: "company",
          label: "Όνομα Εταιρείας",
          type: "text",
          required: true,
          gridSize: 6,
        },
        {
          name: "email",
          label: "Email",
          type: "email",
          required: true,
          gridSize: 6,
          validation: "email",
        },
        {
          name: "phone",
          label: "Τηλέφωνο Επικοινωνίας",
          type: "text",
          required: true,
          gridSize: 6,
          validation: "phone",
          
        },
        {
          name: "second_phone",
          label: "Δεύτερο Τηλέφωνο",
          type: "text",
          gridSize: 6,
          validation: "phone",
       
        },

        {
          name: "registration",
          label: "ΓΕΜΗ",
          type: "text",
          gridSize: 6,
        },
        { name: "extraInfo", label: "ΔΟΥ", type: "text", gridSize: 6 },
        {
          name: "address",
          label: "Διεύθυνση",
          type: "text",
          required: true,
          gridSize: 12,
        },
      ],
    },
    {
      id: "customerOptions",
      title: "Επιλογές Πελάτη",
      fields: [
        {
          name: "pylonCode",
          label: "Κωδικός PYLON",
          type: "number",
          required: true,
          gridSize: 6,
        },
        {
          name: "customerCategory",
          label: "Κατηγορία Πελάτη",
          type: "select",
          required: true,
          options: [
            { label: "Ημερήσιος", value: "DAILY" },
            { label: "Εβδομαδιαίος", value: "WEEKLY" },
          ],
          searchable: false,
          gridSize: 6,
        },

        {
          name: "startTime",
          label: "Ώρα Έναρξης",
          type: "time",
          gridSize: 6,
          conditional: (formData) => formData.customerCategory === "DAILY",
        },
        {
          name: "endTime",
          label: "Ώρα Λήξης",
          type: "time",
          gridSize: 6,
          conditional: (formData) => formData.customerCategory === "DAILY",
        },

        {
          name: "driver",
          label: "Επιλέξτε Οδηγό",
          type: "select",
          options: driverOptions,
          required: true,
          gridSize: 6,
        },
        {
          name: "favouriteProducts",
          label: "Προσθήκη Αγαπημένων Προϊόντων",
          placeholder: "Προσθήκη Αγαπημένων Προϊόντων",
          type: "multi-select",
          options: productOptions,
          gridSize: 12,
        },
      ],
    },
    {
      id: "passwordSection",
      title: "Κωδικός εισόδου στην εφαρμογή",
      fields: [
        {
          name: "newPassword",
          label: "Νέος κωδικός εισόδου",
          type: "password",
          required: !isEdit,
          gridSize: 12,
        },
        {
          name: "confirmPassword",
          label: "Επιβεβαίωση νέου κωδικού",
          type: "password",
          required: !isEdit,
          gridSize: 12,
        },
      ],
    },
  ];

  const [formData, setFormData] = useState({
    fullname: customerData?.fullname || "",
    company: customerData?.company || "",
    email: customerData?.email || "",
    phone: customerData?.phone || "",
    second_phone: customerData?.second_phone || "",
    registration: customerData?.registration || "",
    address: customerData?.address || "",
    pylonCode: customerData?.pylonCode || "",
    customerCategory:
      customerData?.customerCategory?.toUpperCase() === "DAILY"
        ? "DAILY"
        : customerData?.customerCategory?.toUpperCase() === "WEEKLY"
        ? "WEEKLY"
        : "",
    startTime: customerData?.startTime
      ? convert24to12(customerData.startTime)
      : "",
    endTime: customerData?.endTime ? convert24to12(customerData.endTime) : "",

    driver: customerData?.driverId || "",
    selectedDrivers: customerData?.selectedDrivers || [],
    favouriteProducts: customerData?.favouriteProducts || [],
    status: customerData?.status || [],
    newPassword: "",
    confirmPassword: "",
    extraInfo: customerData?.extraInfo || "",
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(customerData?.status || false);

  const handleToggle = () => {
    setStatus((prev) => !prev);

    // Optional: if you want to instantly trigger API update on toggle:
    // updateClientApi({ id: customerData.id, isActive: updatedStatus });
  };

  useEffect(() => {
    if (customerData) {
      console.log("Raw backend startTime:", customerData.startTime); // <== Add this
      setFormData((prev) => ({
        ...prev,
        ...customerData,
        driver: customerData.driverId || "",
        favouriteProducts: customerData.favouriteProducts || [],
        startTime: customerData?.startTime
          ? convert24to12(customerData.startTime)
          : "",
        endTime: customerData?.endTime
          ? convert24to12(customerData.endTime)
          : "",
      }));
    }
  }, [customerData]);

  const validateForm = () => {
    let newErrors = {};
    formConfig.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.required && !formData[field.name]) {
          newErrors[field.name] = "Αυτό το πεδίο είναι υποχρεωτικό";
        }
        if (
          field.validation === "email" &&
          formData[field.name] &&
          !/\S+@\S+\.\S+/.test(formData[field.name])
        ) {
          newErrors[field.name] = "Μη έγκυρη διεύθυνση email";
        }
        if (
          field.validation === "phone" &&
          formData[field.name] &&
          !/^\+30\s\d{3}\s\d{4}\s\d{3}$/.test(formData[field.name])
        ) {
          newErrors[field.name] = "Η μορφή πρέπει να είναι: +30 698 5554 444";
        }
      });
    });
    if (formData.customerCategory === "DAILY") {
      if (!formData.startTime) {
        newErrors.startTime = "Αυτό το πεδίο είναι υποχρεωτικό";
      }
      if (!formData.endTime) {
        newErrors.endTime = "Αυτό το πεδίο είναι υποχρεωτικό";
      }
    }

    if (!isEdit) {
      if (!formData.newPassword) {
        newErrors.newPassword = "Αυτό το πεδίο είναι υποχρεωτικό";
      }
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = "Αυτό το πεδίο είναι υποχρεωτικό";
      }
    }

    if (
      formData.newPassword &&
      formData.newPassword !== formData.confirmPassword
    ) {
      newErrors.confirmPassword = "Οι κωδικοί δεν ταιριάζουν";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const isUpdate = !!customerData?.id;

    const payload = {
      role: "CLIENT",
      name: formData.fullname,
      companyName: formData.company,
      email: formData.email,
      mobile: formData.phone,
      secondaryMobile: formData.second_phone,
      regNo: formData.registration,
      password: formData.newPassword || customerData?.password || "", // used only when adding
      address: formData.address,
      category: formData.customerCategory === "DAILY" ? 1 : 2,
      driver: formData.driver,
      paylonCode: formData.pylonCode,
      favouriteProducts: formData.favouriteProducts,
      orderStartTime: convert12to24(formData.startTime),
      orderEndTime: convert12to24(formData.endTime),
      isActive: status,
      extraInfo: formData.extraInfo,
    };

    try {
      // Step 1: Update or Add Client
      const response = isUpdate
        ? await updateClientApi({
            id: customerData.id,
            isActive: status,
            ...payload,
          }).unwrap()
        : await addClientApi(payload).unwrap();

      console.log("Client API response:", response);

      // Step 2: Reset password only on update and if a new password was entered
      if (isUpdate && formData.newPassword) {
        const resetPayload = {
          userId: customerData.id,
          password: formData.newPassword,
          confirmPassword: formData.confirmPassword,
        };

        try {
          const resetResponse = await resetPassword(resetPayload).unwrap();
          console.log("Password reset successful:", resetResponse);
        } catch (resetError) {
          console.error("Password reset failed:", resetError);
          toast({
            title: "Password Reset Failed",
            description:
              resetError.data?.message || "Αποτυχία ενημέρωσης κωδικού",
            status: "error",
            duration: 4000,
            isClosable: true,
          });
          return;
        }
      }

      // Final toast for overall success
      toast({
        title: "Success",
        description: response.message || "Οδηγός δημιουργήθηκε με επιτυχία",
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      onAddCustomerStatus?.(response.success);
    } catch (error) {
      console.error("Main API Error:", error);
      toast({
        title: "Error",
        description: error.data?.message || "Αποτυχία δημιουργίας οδηγού",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <Box mt={24}>
      {/* {isEdit && ( */}
      <ProfileCard
        name={formData.fullname}
        email={formData.email}
        profileImage={formData.clientImage || ""}
        isActive={status}
        onToggle={() => handleToggle()} // remove index from function
      />

      {loadingDrivers || loadingProducts ? (
        <Center mt={12}>
          <Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
        </Center>
      ) : (
        <>
          <Grid mt={4} templateColumns="65% 33%" gap={6} alignItems="start">
            <GridItem>
              <VStack spacing={6} align="stretch">
                {formConfig.slice(0, 2).map((card) => (
                  <DynamicFormContainer
                    key={card.id}
                    title={card.title}
                    cardData={card}
                    formData={formData}
                    setFormData={setFormData}
                    errors={errors}
                    minHeight="600px"
                  />
                ))}
              </VStack>
            </GridItem>
            <GridItem>
              <DynamicFormContainer
                title={formConfig[2].title}
                cardData={formConfig[2]}
                formData={formData}
                setFormData={setFormData}
                errors={errors}
              />
            </GridItem>
          </Grid>

          {Object.keys(errors).length > 0 && (
            <Text color="red.500" mt={4}>
              Υπάρχουν σφάλματα στη φόρμα. Ελέγξτε τα πεδία και προσπαθήστε
              ξανά.
            </Text>
          )}

          <Button
            mt={6}
            variant="no-hover"
            bg="linear-gradient(81.62deg, #313860 2.25%, #151928 79.87%)"
            minW="125px"
            h="35px"
            color="#fff"
            fontSize="xs"
            fontWeight="bold"
            alignSelf={{ sm: "flex-start", lg: null }}
            mb={{ sm: "12px", md: "0px" }}
            onClick={handleSubmit}
          >
            {isEdit ? "Ενημέρωση Πελάτη" : "Προσθήκη Πελάτη"}
          </Button>
        </>
      )}
      {/* <CustomTimePicker value={value} onChange={setValue} /> */}
      {/* <div style={{ padding: '20px' }}>
      <CustomTimePicker value={time} onChange={setTime} />
      <p>Selected Time: {time}</p>
    </div> */}
    </Box>
  );
};

export default AddCustomer;
